import { atom } from "jotai";
import type {
  BuildDateSummary,
  BuiltRoute,
  UnassignedBuildOrder,
} from "@/types/routes";

/** Shape of an in-progress build-route task. */
export interface BuildRouteTask {
  taskId: string;
  /** Progress percentage 0–100, null when not yet reported. */
  progressPct: number | null;
}

/**
 * Null when no build-route task is in progress.
 * Set to the task data immediately after POST /api/v1/routes/build/ returns 202.
 * Cleared when the task completes or fails.
 */
export const buildRouteTaskAtom = atom<BuildRouteTask | null>(null);

/**
 * Orders that could not be assigned to any route during the last auto-build.
 * Non-empty when the completed build result contains unassigned orders.
 * Cleared when the user dismisses the alert panel.
 */
export const unassignedBuildOrdersAtom = atom<UnassignedBuildOrder[]>([]);

/**
 * Routes created by the last successful build.
 * Non-empty when the completed build result produced at least one route.
 * Populated by the build poller on completion and consumed by
 * `BuildRoutesSummaryAlert` to show a green per-route summary on the routes list.
 * Cleared when the user dismisses the summary panel.
 */
export const builtRoutesAtom = atom<BuiltRoute[]>([]);

/**
 * Per-delivery-date summary of the last successful build.
 * Non-empty when the completed build result carried a `by_date` breakdown.
 * Populated by the build poller on completion alongside `builtRoutesAtom` and
 * consumed by `BuildRoutesSummaryAlert` to show that routes were split across
 * delivery dates and that overdue orders were pulled forward onto today.
 * Cleared alongside `builtRoutesAtom` when the user dismisses the summary panel.
 */
export const builtRoutesByDateAtom = atom<BuildDateSummary[]>([]);
